export const APP_VERSION_QUERY = "__app_version";

// Each hosting site serves its own version marker. Polling never uses another
// domain, and a cache-busting navigation replaces rather than extends history.
export function startAppVersionMonitor({
  buildId,
  canReload = () => true,
  prepareReload = async () => true,
  onUpdateAvailable = () => {},
  fetchVersion = (url, options) => fetch(url, options),
  reload = (url) => window.location.replace(url),
  intervalMs = 60000,
}) {
  if (!buildId) return { check: () => Promise.resolve(), stop: () => {} };
  let stopped = false;
  let checking = false;
  let reloading = false;
  let nextBuildId = null;
  let idleTimer;
  let lastInteraction = 0;
  let attemptedBuild = null;
  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get(APP_VERSION_QUERY) === buildId) {
    currentUrl.searchParams.delete(APP_VERSION_QUERY);
    window.history.replaceState(window.history.state, "", currentUrl);
  }
  try { attemptedBuild = window.sessionStorage.getItem("appUpdateAttempt"); } catch (_) {}

  const attemptReload = async () => {
    if (stopped || reloading || !nextBuildId || `${buildId}->${nextBuildId}` === attemptedBuild || document.visibilityState === "hidden" || navigator.onLine === false) return;
    if (Date.now() - lastInteraction < 2000 || !canReload()) return;
    reloading = true;
    try {
      if (!await prepareReload() || stopped || !canReload() || Date.now() - lastInteraction < 2000) return;
      attemptedBuild = `${buildId}->${nextBuildId}`;
      try { window.sessionStorage.setItem("appUpdateAttempt", attemptedBuild); } catch (_) {}
      const url = new URL(window.location.href);
      url.searchParams.set(APP_VERSION_QUERY, nextBuildId);
      reload(url.toString());
    } catch (_) {
      // Network/save failures defer the update; they never discard a draft.
    } finally { reloading = false; }
  };

  const check = async () => {
    if (stopped || checking || document.visibilityState === "hidden" || navigator.onLine === false) return;
    checking = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetchVersion(`/app-version.json?check=${Date.now()}`, { cache: "no-store", signal: controller.signal });
      if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) return;
      const version = await response.json();
      if (stopped || typeof version.buildId !== "string" || !/^[\w.-]{1,100}$/.test(version.buildId)) return;
      nextBuildId = version.buildId === buildId ? null : version.buildId;
      onUpdateAvailable(Boolean(nextBuildId));
      await attemptReload();
    } catch (_) {
      // Offline, missing version files and interrupted deploys are harmless.
    } finally { clearTimeout(timeout); checking = false; }
  };
  const interact = () => {
    lastInteraction = Date.now();
    clearTimeout(idleTimer);
    // Reconfirm the deployment before navigating; a previously detected update
    // must not replace a working page after the connection has since dropped.
    idleTimer = setTimeout(() => { if (nextBuildId) check(); }, 2100);
  };
  const resume = () => { check(); };
  const events = ["input", "change", "pointerdown", "keydown", "focusout", "dragstart", "drop"];
  events.forEach((event) => document.addEventListener(event, interact, true));
  window.addEventListener("focus", resume);
  window.addEventListener("pageshow", resume);
  window.addEventListener("online", resume);
  document.addEventListener("visibilitychange", resume);
  const interval = setInterval(check, intervalMs);
  check();
  return {
    check,
    stop: () => {
      stopped = true;
      clearInterval(interval); clearTimeout(idleTimer);
      events.forEach((event) => document.removeEventListener(event, interact, true));
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    },
  };
}
